import React, { useEffect, useState } from "react";
import { Button, Card, Input, message, Spin, Table, Modal } from "antd";
import {
  Award,
  BookOpen,
  ChevronLeft,
  ChevronRight,
  Eye,
  FileText,
  Filter,
  GraduationCap,
  Mail,
  RefreshCw,
  SchoolIcon,
  Search,
  User,
  Users,
  AlertCircle,
  Plus,
  Edit3,
  CheckCircle,
  Archive,
  Calendar,
  Clock,
  TrendingUp,
  X
} from "lucide-react";
import { MdGrade } from "react-icons/md";
import TeacherHPCReportPage from "./foundational_report/TeacherHPCReportPage";
import { API_BASE_URL } from "../../config";
import ActionIconButton from "../../Components/ActionIconButton";
import ActionsCell from "../../Components/ActionsCell";

// Constants for colors - matching LessonPlanHistory.js
const colors = {
  orange: "#D67C2C",
  blue: "#2173B7",
  accent: "#F7FAFF"
};

// Helper function to get user initials
function getInitials(name = "") {
  return name
    .split(" ")
    .map((n) => n[0])
    .join("")
    .toUpperCase()
    .slice(0, 2);
}

function StudentReport() {
  // State management
  const [students, setStudents] = useState([]);
  const [filteredStudents, setFilteredStudents] = useState([]);
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState(null);
  const [selectedStudent, setSelectedStudent] = useState(null);
  const [hpcModalVisible, setHpcModalVisible] = useState(false);
  const [searchText, setSearchText] = useState("");
  const [currentPage, setCurrentPage] = useState(1);
  const [retryCount, setRetryCount] = useState(0);
  const [errorModalVisible, setErrorModalVisible] = useState(false);
  const [notification, setNotification] = useState(null);

  // Filter state - matches LessonPlanHistory.js
  const [filters, setFilters] = useState({
    grade: "",
    school: "",
    status: "",
    enrollmentStartDate: "",
    enrollmentEndDate: ""
  });

  // Pagination settings
  const pageSize = 10;
  const totalPages = Math.ceil(filteredStudents.length / pageSize);
  const totalStudents = filteredStudents.length;
  const currentStudents = filteredStudents.slice(
    (currentPage - 1) * pageSize,
    currentPage * pageSize
  );

  // Stats for dashboard - similar to LessonPlanHistory.js
  const [stats, setStats] = useState({
    totalStudents: 0,
    activeStudents: 0,
    completedReports: 0
  });

  // Get access token
  const accessToken = localStorage.getItem("accessToken");

  // Fetch students on component mount with retry logic
  useEffect(() => {
    const fetchWithRetry = async () => {
      try {
        await fetchStudents();
      } catch (error) {
        // If we have retries left and there was an error, try again after a delay
        if (retryCount < 3) {
          console.log(`Retrying fetch (${retryCount + 1}/3)...`);
          setTimeout(() => {
            setRetryCount(prevCount => prevCount + 1);
          }, 1500); // Retry after 1.5 seconds
        } else {
          // If we've exhausted retries, show the error modal
          setErrorModalVisible(true);
        }
      }
    };

    fetchWithRetry();
    // eslint-disable-next-line
  }, [retryCount]);

  // Update filtered students when students or search/filters change
  useEffect(() => {
    filterStudents();
    updateStats();
    // eslint-disable-next-line
  }, [students, searchText, filters]);

  // Filter students based on search and filter criteria
  const filterStudents = () => {
    const filtered = students.filter(student => {
      // Text search
      const matchesSearch = !searchText ||
        student.name?.toLowerCase().includes(searchText.toLowerCase()) ||
        student.email?.toLowerCase().includes(searchText.toLowerCase());

      // Grade filter
      const matchesGrade = !filters.grade || student.grade === filters.grade;

      // School filter
      const matchesSchool = !filters.school || student.school_name === filters.school;

      // Status filter (active/inactive)
      const matchesStatus = !filters.status || student.status === filters.status;

      // Enrollment date range filter
      const enrollmentDateValue = student.enrollment_date || student.createdAt;
      const enrollmentDate = enrollmentDateValue ? new Date(enrollmentDateValue) : null;
      const matchesEnrollmentStart = !filters.enrollmentStartDate ||
        (enrollmentDate && enrollmentDate >= new Date(filters.enrollmentStartDate));
      const matchesEnrollmentEnd = !filters.enrollmentEndDate ||
        (enrollmentDate && enrollmentDate <= new Date(`${filters.enrollmentEndDate}T23:59:59`));

      return matchesSearch && matchesGrade && matchesSchool && matchesStatus &&
        matchesEnrollmentStart && matchesEnrollmentEnd;
    });

    setFilteredStudents(filtered);
  };

  // Update dashboard stats
  const updateStats = () => {
    setStats({
      totalStudents: students.length,
      activeStudents: students.filter(s => s.status === 'active').length || students.length,
      completedReports: students.length || 0
    });
  };

  // Fetch all users (admin endpoint), filter only students
  const fetchStudents = async () => {
    setLoading(true);
    setError(null);

    try {
      if (!accessToken) {
        throw new Error("Authentication token not found");
      }

      const response = await fetch(`${API_BASE_URL}/admin/get-users`, {
        method: "GET",
        headers: { Authorization: `Bearer ${accessToken}` },
      });

      if (!response.ok) {
        throw new Error(`Failed to fetch users: ${response.status} ${response.statusText}`);
      }

      const users = await response.json();
      const studentUsers = users.filter(user => user.role === "student");

      setStudents(studentUsers);
      setFilteredStudents(studentUsers);
      setError(null);
      setErrorModalVisible(false);
    } catch (error) {
      console.error("Error fetching users:", error);
      setError(error.message || "Error fetching students");
      throw error; // Re-throw for retry logic
    } finally {
      setLoading(false);
    }
  };

  // Handle pagination - exactly like LessonPlanHistory.js
  const handlePreviousPage = () => {
    if (currentPage > 1) {
      setCurrentPage(currentPage - 1);
    }
  };

  const handleNextPage = () => {
    if (currentPage < totalPages) {
      setCurrentPage(currentPage + 1);
    }
  };

  // Handle view HPC report
  const handleViewHPCReport = (student) => {
    setSelectedStudent(student);
    setHpcModalVisible(true);
  };

  // Handle search input - matching LessonPlanHistory.js style
  const handleSearch = (e) => {
    e.preventDefault(); // Prevent form submission
    // Search is already handled by the useEffect when searchText changes
  };

  // Handle filter changes - matching LessonPlanHistory.js style
  const handleFilterChange = (e) => {
    const { name, value } = e.target;
    setFilters(prev => ({
      ...prev,
      [name]: value
    }));
    setCurrentPage(1); // Reset to first page when filters change
  };

  // Clear all filters - matching LessonPlanHistory.js style
  const clearFilters = () => {
    setFilters({
      grade: "",
      school: "",
      status: "",
      enrollmentStartDate: "",
      enrollmentEndDate: ""
    });
    setSearchText("");
    setCurrentPage(1);
  };

  // Distinct school names from loaded students - matches StudentView.js Section filter pattern
  const distinctSchools = Array.from(
    new Set(students.map(s => s.school_name).filter(Boolean))
  ).sort();

  // Retry fetching students
  const handleRetryFetch = () => {
    setRetryCount(0); // Reset retry count
    setErrorModalVisible(false); // Close modal
  };

  // Show notification - matching LessonPlanHistory.js
  const showNotification = (type, message) => {
    setNotification({ type, message });
    setTimeout(() => setNotification(null), 5000);
  };

  // Format date for display - matching LessonPlanHistory.js
  const formatDate = (dateString) => {
    if (!dateString) return 'N/A';
    const date = new Date(dateString);
    return date.toLocaleDateString('en-US', {
      year: 'numeric',
      month: 'short',
      day: 'numeric'
    });
  };

  // Get status badge - similar to LessonPlanHistory.js
  const getStatusBadge = (status) => {
    switch (status) {
      case 'active':
        return (
          <span className="px-3 py-1 rounded-full text-xs font-medium bg-green-100 text-green-800">
            <CheckCircle className="w-3 h-3 inline-block mr-1" />
            Active
          </span>
        );
      case 'inactive':
        return (
          <span className="px-3 py-1 rounded-full text-xs font-medium bg-yellow-100 text-yellow-800">
            <Edit3 className="w-3 h-3 inline-block mr-1" />
            Inactive
          </span>
        );
      default:
        return (
          <span className="px-3 py-1 rounded-full text-xs font-medium bg-gray-100 text-gray-800">
            {status || 'Unknown'}
          </span>
        );
    }
  };

  return (
    <>
      <div className="bg-white p-8 min-h-screen rounded-b-2xl rounded-tr-2xl shadow-xl font-[poppins]">
        <div className="w-full px-4 sm:px-6 lg:px-8 py-8">

          {/* Notification - exactly like LessonPlanHistory.js */}
          {notification && (
            <div
              className={`fixed top-4 right-4 z-50 p-4 rounded-lg shadow-lg flex items-center gap-3 transition-all duration-300 ${notification.type === 'success' ? 'bg-green-500 text-white' :
                  notification.type === 'error' ? 'bg-red-500 text-white' :
                    'bg-blue-500 text-white'
                }`} style={{ animation: 'slideInRight 0.5s ease-out' }}>
              {notification.type === 'success' ? <CheckCircle className="w-5 h-5" /> :
                notification.type === 'error' ? <AlertCircle className="w-5 h-5" /> :
                  <AlertCircle className="w-5 h-5" />}
              <span className="font-[poppins]">{notification.message}</span>
              <button onClick={() => setNotification(null)} className="ml-2 hover:opacity-80">
                <X className="w-4 h-4" />
              </button>
            </div>
          )}

          {/* Header - exactly like LessonPlanHistory.js */}
          <div className="pt-8 pb-8 text-center relative overflow-hidden">
            <div className="relative z-10">
              <div>
                <div className="inline-flex items-center justify-center w-16 h-16 rounded-full mb-6"
                  style={{ backgroundColor: '#D67C2C' }}>
                  <FileText className="w-8 h-8 text-white" />
                </div>
                <h1 className="text-5xl font-bold bg-[#D67C2C] bg-clip-text text-transparent mb-4">
                  Student Reports Dashboard
                </h1>
                <p className="text-[#1D3469] text-xl font-medium max-w-3xl mx-auto px-4">
                  Comprehensive student performance tracking and report generation
                </p>
              </div>
            </div>
          </div>

          {/* Dashboard Cards - exactly like LessonPlanHistory.js */}
          <div className="grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-3 gap-6 mb-10">
            <div className="bg-white rounded-xl shadow-md overflow-hidden border border-gray-200 transition-all duration-300 hover:shadow-lg">
              <div className="p-6">
                <div className="flex items-center justify-between">
                  <div>
                    <p className="text-sm font-medium text-gray-500">Total Students</p>
                    <p className="text-3xl font-bold text-gray-900 mt-1">{stats.totalStudents}</p>
                  </div>
                  <div className="p-3 rounded-full bg-blue-100">
                    <Users className="w-6 h-6 text-blue-600" />
                  </div>
                </div>
              </div>
            </div>

            <div className="bg-white rounded-xl shadow-md overflow-hidden border border-gray-200 transition-all duration-300 hover:shadow-lg">
              <div className="p-6">
                <div className="flex items-center justify-between">
                  <div>
                    <p className="text-sm font-medium text-gray-500">Active Students</p>
                    <p className="text-3xl font-bold text-green-600 mt-1">{stats.activeStudents}</p>
                  </div>
                  <div className="p-3 rounded-full bg-green-100">
                    <CheckCircle className="w-6 h-6 text-green-600" />
                  </div>
                </div>
              </div>
            </div>

            <div className="bg-white rounded-xl shadow-md overflow-hidden border border-gray-200 transition-all duration-300 hover:shadow-lg">
              <div className="p-6">
                <div className="flex items-center justify-between">
                  <div>
                    <p className="text-sm font-medium text-gray-500">Completed Reports</p>
                    <p className="text-3xl font-bold text-blue-600 mt-1">{stats.completedReports}</p>
                  </div>
                  <div className="p-3 rounded-full bg-blue-100">
                    <FileText className="w-6 h-6 text-blue-600" />
                  </div>
                </div>
              </div>
            </div>
          </div>

          {/* Filter Section - exactly like LessonPlanHistory.js */}
          <div className="bg-white rounded-2xl shadow-md overflow-hidden border border-gray-200 mb-8">
            <div className="p-6 border-b border-gray-100 bg-gray-50">
              <div className="flex flex-wrap items-center justify-between gap-4">
                <div className="flex items-center gap-3">
                  <div className="w-10 h-10 rounded-xl flex items-center justify-center bg-[#D67C2C]">
                    <Filter className="w-5 h-5 text-white" />
                  </div>
                  <h2 className="text-xl font-semibold text-gray-900 font-[poppins]">Filter Students</h2>
                </div>

                <button
                  onClick={() => showNotification('info', 'Create new student feature coming soon!')}
                  className="flex items-center gap-2 px-4 py-2 bg-[#D67C2C] text-white rounded-lg hover:bg-[#c06a25] transition-colors"
                >
                  <Plus className="w-5 h-5" />
                  Create New Student
                </button>
              </div>
            </div>

            <div className="p-6">
              <form onSubmit={handleSearch}
                className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-2 gap-2">
                <div className="space-y-2">
                  <label className="block text-sm font-medium text-gray-700">
                    <Search className="inline-block w-4 h-4 mr-2" />
                    Search
                  </label>
                  <div className="relative">
                    <input
                      type="text"
                      name="search"
                      value={searchText}
                      onChange={(e) => setSearchText(e.target.value)}
                      placeholder="Search by name or email..."
                      className="w-full pl-10 pr-4 py-2 border border-gray-300 rounded-lg focus:ring-2 focus:ring-[#D67C2C] focus:border-transparent"
                    />
                    <Search className="absolute left-3 top-2.5 w-4 h-4 text-gray-400" />
                  </div>
                </div>

                <div className="space-y-2">
                  <label className="block text-sm font-medium text-gray-700">
                    <CheckCircle className="inline-block w-4 h-4 mr-2" />
                    Status
                  </label>
                  <select
                    name="status"
                    value={filters.status}
                    onChange={handleFilterChange}
                    className="w-full px-4 py-2 border border-gray-300 rounded-lg focus:ring-2 focus:ring-[#D67C2C] focus:border-transparent"
                  >
                    <option value="">All Statuses</option>
                    <option value="active">Active</option>
                    <option value="inactive">Inactive</option>
                  </select>
                </div>

                <div className="space-y-2">
                  <label className="block text-sm font-medium text-gray-700">
                    <GraduationCap className="inline-block w-4 h-4 mr-2" />
                    Grade
                  </label>
                  <select
                    name="grade"
                    value={filters.grade}
                    onChange={handleFilterChange}
                    className="w-full px-4 py-2 border border-gray-300 rounded-lg focus:ring-2 focus:ring-[#D67C2C] focus:border-transparent"
                  >
                    <option value="">All Grades</option>
                    <option value="Nursery">Nursery</option>
                    <option value="LKG">LKG</option>
                    <option value="UKG">UKG</option>
                    <option value="1">Grade 1</option>
                    <option value="2">Grade 2</option>
                    <option value="3">Grade 3</option>
                    <option value="4">Grade 4</option>
                    <option value="5">Grade 5</option>
                    <option value="6">Grade 6</option>
                    <option value="7">Grade 7</option>
                    <option value="8">Grade 8</option>
                  </select>
                </div>

                <div className="space-y-2">
                  <label className="block text-sm font-medium text-gray-700">
                    <SchoolIcon className="inline-block w-4 h-4 mr-2" />
                    School
                  </label>
                  <select
                    name="school"
                    value={filters.school}
                    onChange={handleFilterChange}
                    className="w-full px-4 py-2 border border-gray-300 rounded-lg focus:ring-2 focus:ring-[#D67C2C] focus:border-transparent"
                  >
                    <option value="">All Schools</option>
                    {distinctSchools.map(school => (
                      <option key={school} value={school}>{school}</option>
                    ))}
                  </select>
                </div>

                <div className="space-y-2">
                  <label className="block text-sm font-medium text-gray-700">
                    <Calendar className="inline-block w-4 h-4 mr-2" />
                    Enrollment Date From
                  </label>
                  <input
                    type="date"
                    name="enrollmentStartDate"
                    value={filters.enrollmentStartDate}
                    onChange={handleFilterChange}
                    className="w-full px-4 py-2 border border-gray-300 rounded-lg focus:ring-2 focus:ring-[#D67C2C] focus:border-transparent"
                  />
                </div>

                <div className="space-y-2">
                  <label className="block text-sm font-medium text-gray-700">
                    <Calendar className="inline-block w-4 h-4 mr-2" />
                    Enrollment Date To
                  </label>
                  <input
                    type="date"
                    name="enrollmentEndDate"
                    value={filters.enrollmentEndDate}
                    onChange={handleFilterChange}
                    className="w-full px-4 py-2 border border-gray-300 rounded-lg focus:ring-2 focus:ring-[#D67C2C] focus:border-transparent"
                  />
                </div>

                <div className="md:col-span-3 lg:col-span-6 flex items-center justify-center space-x-4 mt-4">
                  <button
                    type="submit"
                    className="px-6 py-2 bg-blue-600 text-white rounded-lg hover:bg-blue-700 transition-colors"
                  >
                    <Search className="inline-block w-4 h-4 mr-2" />
                    Search
                  </button>

                  <button
                    type="button"
                    onClick={clearFilters}
                    className="px-6 py-2 border border-gray-300 text-gray-700 rounded-lg hover:bg-gray-100 transition-colors"
                  >
                    <RefreshCw className="inline-block w-4 h-4 mr-2" />
                    Clear Filters
                  </button>
                </div>
              </form>
            </div>
          </div>

          {/* Students Table - exactly like LessonPlanHistory.js table */}
          <div className="bg-white rounded-2xl shadow-md overflow-hidden border border-gray-200 mb-8">
            <div className="p-6 border-b border-gray-100 bg-gray-50">
              <div className="flex items-center gap-3">
                <div className="w-10 h-10 rounded-xl flex items-center justify-center bg-[#D67C2C]">
                  <Users className="w-5 h-5 text-white" />
                </div>
                <h2 className="text-xl font-semibold text-gray-900 font-[poppins]">
                  Student List ({filteredStudents.length})
                </h2>
              </div>
            </div>

            {loading ? (
              <div className="flex justify-center items-center p-12">
                <div className="w-8 h-8 text-[#D67C2C] animate-spin">
                  <RefreshCw className="w-8 h-8" />
                </div>
                <span className="ml-3 text-lg text-gray-600">Loading students...</span>
              </div>
            ) : error ? (
              <div className="flex justify-center items-center p-12 text-red-500">
                <AlertCircle className="w-6 h-6 mr-2" />
                <span>{error}</span>
                <button
                  onClick={handleRetryFetch}
                  className="ml-4 px-4 py-2 bg-[#D67C2C] text-white rounded-lg hover:bg-[#c06a25] transition-colors"
                >
                  Retry
                </button>
              </div>
            ) : filteredStudents.length === 0 ? (
              <div className="flex flex-col justify-center items-center p-12 text-gray-500">
                <User className="w-16 h-16 mb-4 text-gray-300" />
                <p className="text-lg mb-3">No students found</p>
                <p className="text-sm text-gray-400 mb-6">
                  {filters.status || filters.school || filters.grade || filters.enrollmentStartDate || filters.enrollmentEndDate || searchText ?
                    "Try clearing your filters or creating a new student." :
                    "Get started by creating your first student."}
                </p>
                <button
                  onClick={() => showNotification('info', 'Create student feature coming soon!')}
                  className="flex items-center gap-2 px-4 py-2 bg-[#D67C2C] text-white rounded-lg hover:bg-[#c06a25] transition-colors"
                >
                  <Plus className="w-5 h-5" />
                  Create New Student
                </button>
              </div>
            ) : (
              <div className="overflow-x-auto">
                <table className="min-w-full divide-y divide-gray-200">
                  <thead className="bg-gray-50">
                    <tr>
                      <th scope="col" className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">
                        Student
                      </th>
                      <th scope="col" className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">
                        Email
                      </th>
                      <th scope="col" className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">
                        Status
                      </th>
                      <th scope="col" className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">
                        Enrollment Date
                      </th>
                      <th scope="col" className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">
                        Grade/School
                      </th>
                      <th scope="col" className="px-6 py-3 text-right text-xs font-medium text-gray-500 uppercase tracking-wider">
                        Actions
                      </th>
                    </tr>
                  </thead>
                  <tbody className="bg-white divide-y divide-gray-200">
                    {currentStudents.map((student) => (
                      <tr key={student.id} className="hover:bg-gray-50 transition-colors">
                        <td className="px-6 py-4 whitespace-nowrap">
                          <div className="flex items-center">
                            <div className="flex-shrink-0 h-30 w-30 rounded-lg flex items-center justify-center">
                              {student.profile_picture_url ? (
                                <img
                                  src={API_BASE_URL + student.profile_picture_url}
                                  alt={student.name}
                                  className="h-20 w-20 rounded-lg object-cover"
                                />
                              ) : (
                                <div className="h-10 w-10 rounded-full bg-[#FFF8F4] flex items-center justify-center text-lg text-[#F17C45] font-semibold border-2 border-[#F7D1B6]">
                                  {getInitials(student.name)}
                                </div>
                              )}
                            </div>
                            <div className="ml-4">
                              <div className="text-sm font-medium text-gray-900">
                                {student.name || 'Unnamed Student'}
                              </div>
                              <div className="text-sm text-gray-500">
                                ID: {student.id ? String(student.id).substring(0, 8) : "N/A"}
                              </div>
                            </div>
                          </div>
                        </td>
                        <td className="px-6 py-4 whitespace-nowrap">
                          <div className="flex items-center">
                            <div>
                              <div className="text-sm text-gray-900 flex items-center">
                                <Mail className="w-4 h-4 mr-1 text-[#D67C2C]" />
                                {student.email || 'No email'}
                              </div>
                            </div>
                          </div>
                        </td>
                        <td className="px-6 py-4 whitespace-nowrap">
                          {getStatusBadge(student.status || 'active')}
                        </td>
                        <td className="px-6 py-4 whitespace-nowrap text-sm text-gray-500">
                          <div className="flex items-center">
                            <Calendar className="w-4 h-4 mr-1 text-gray-400" />
                            {formatDate(student.enrollment_date || student.createdAt)}
                          </div>
                        </td>
                        <td className="px-6 py-4 whitespace-nowrap">
                          <div className="flex flex-col">
                            <div className="text-sm font-medium text-gray-900 flex items-center">
                              <GraduationCap className="w-4 h-4 mr-1 text-blue-600" />
                              Grade {student.grade || 'N/A'}
                            </div>
                            <div className="text-sm text-gray-500 flex items-center mt-1">
                              <BookOpen className="w-4 h-4 mr-1 text-green-600" />
                              {student.school_name || 'N/A'}
                            </div>
                          </div>
                        </td>
                        <td className="px-6 py-4 whitespace-nowrap text-right text-sm font-medium">
                          <ActionsCell className="justify-end">
                            <ActionIconButton
                              icon={Eye}
                              tone="view"
                              onClick={() => handleViewHPCReport(student)}
                              title="View Report"
                            />
                          </ActionsCell>
                        </td>
                      </tr>
                    ))}
                  </tbody>
                </table>
              </div>
            )}

            {/* Pagination - exactly like LessonPlanHistory.js */}
            {!loading && !error && filteredStudents.length > 0 && (
              <div className="px-6 py-4 bg-gray-50 border-t border-gray-200 flex items-center justify-between">
                <div className="text-sm text-gray-500">
                  Showing {((currentPage - 1) * pageSize) + 1} to {Math.min(currentPage * pageSize, totalStudents)} of {totalStudents} students
                </div>
                <div className="flex space-x-2">
                  <button
                    onClick={handlePreviousPage}
                    disabled={currentPage === 1}
                    className={`p-2 rounded-md ${currentPage === 1 ? 'text-gray-400 cursor-not-allowed' : 'text-gray-600 hover:bg-gray-200'}`}
                  >
                    <ChevronLeft className="w-5 h-5" />
                  </button>

                  {[...Array(Math.min(totalPages, 5))].map((_, i) => {
                    // Show pages around current page
                    let pageNum;
                    if (totalPages <= 5) {
                      pageNum = i + 1;
                    } else if (currentPage <= 3) {
                      pageNum = i + 1;
                    } else if (currentPage >= totalPages - 2) {
                      pageNum = totalPages - 4 + i;
                    } else {
                      pageNum = currentPage - 2 + i;
                    }

                    return (
                      <button
                        key={i}
                        onClick={() => setCurrentPage(pageNum)}
                        className={`px-3 py-1 rounded-md ${pageNum === currentPage ? 'bg-[#D67C2C] text-white' : 'text-gray-600 hover:bg-gray-200'}`}
                      >
                        {pageNum}
                      </button>
                    );
                  })}

                  <button
                    onClick={handleNextPage}
                    disabled={currentPage === totalPages}
                    className={`p-2 rounded-md ${currentPage === totalPages ? 'text-gray-400 cursor-not-allowed' : 'text-gray-600 hover:bg-gray-200'}`}
                  >
                    <ChevronRight className="w-5 h-5" />
                  </button>
                </div>
              </div>
            )}
          </div>

          {/* Error Modal - similar to LessonPlanHistory.js DeleteConfirmationModal */}
          <Modal
            title={
              <div className="flex items-center gap-2 text-red-600">
                <AlertCircle className="w-5 h-5" />
                <span>Error Loading Students</span>
              </div>
            }
            open={errorModalVisible}
            onCancel={() => setErrorModalVisible(false)}
            footer={[
              <Button key="retry" type="primary" onClick={handleRetryFetch} style={{ backgroundColor: '#D67C2C', border: 'none' }}>
                Retry
              </Button>,
              <Button key="cancel" onClick={() => setErrorModalVisible(false)}>
                Close
              </Button>,
            ]}
          >
            <div className="py-4">
              <p className="mb-4">Failed to fetch students. This could be due to:</p>
              <ul className="list-disc pl-5 space-y-2">
                <li>Network connection issue</li>
                <li>Authentication token not fully loaded</li>
                <li>Server temporarily unavailable</li>
              </ul>
              <p className="mt-4">Please try refreshing the page or click "Retry" to attempt loading again.</p>
            </div>
          </Modal>

          {/* HPC Report Modal */}
          <TeacherHPCReportPage
            studentEmail={selectedStudent?.email}
            studentName={selectedStudent?.name}
            visible={hpcModalVisible}
            onClose={() => setHpcModalVisible(false)}
          />
        </div>
      </div>
    </>
  );
}

export default StudentReport;